import { WorkAvailability_userProfile$key } from '#app/__generated__/relay/WorkAvailability_userProfile.graphql.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { Text } from '#app/components/Primitives/Text.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { WorkAvailabilityTag } from './WorkAvailabilityTag.js';
import { graphql, useFragment } from '@contra/relay';
import { useEffect } from 'react';
import { styled } from 'styled-components';

const TooltipText = styled(Text)`
  text-align: left;
`;

type WorkAvailabilityProps = {
  readonly userProfileRef: WorkAvailability_userProfile$key;
};

export const WorkAvailability = ({ userProfileRef }: WorkAvailabilityProps) => {
  const [urlParameters, setURLParameters] = useURLParameters('/@username');
  const openAvailabilityModal = urlParameters.updateAvailability;
  const { track } = useAnalytics();

  const userProfile = useFragment<WorkAvailability_userProfile$key>(
    graphql`
      fragment WorkAvailability_userProfile on UserProfile {
        visitorCanEdit
        workPreferences {
          isCurrentlyAvailable
        }
        ...UpdateAvailabilityModal_userProfile
        ...WorkAvailabilityTag_userProfile
      }
    `,
    userProfileRef,
  );

  const {
    visitorCanEdit,
    workPreferences: { isCurrentlyAvailable },
  } = userProfile;

  const { openModal } = useModal({
    identifier: 'UpdateAvailabilityModal',
    modalProps: {
      userProfileRef: userProfile,
    },
  });

  useEffect(() => {
    if (openAvailabilityModal) {
      track('modal', {
        action: 'viewed',
        modal_name: 'update_availability_modal',
      });
      openModal();
      setURLParameters({
        updateAvailability: undefined,
      });
    }
  }, [openAvailabilityModal, openModal, setURLParameters, track]);

  if (visitorCanEdit) {
    return (
      <Tooltip
        content={
          isCurrentlyAvailable ? (
            <TooltipText>
              You are currently available. Setting your availability boosts your
              visibility to clients and is required to appear in top search
              results. <br /> <br />{' '}
              <strong>Click to set your availability.</strong>
            </TooltipText>
          ) : (
            <TooltipText>
              You are currently unavailable. Setting your availability boosts
              your visibility to clients and is required to appear in top search
              results. <br /> <br />{' '}
              <strong>Click to set your availability.</strong>{' '}
            </TooltipText>
          )
        }
        disableOpenOnFocus
      >
        <WorkAvailabilityTag userProfileRef={userProfile} />
      </Tooltip>
    );
  } else if (isCurrentlyAvailable) {
    return <WorkAvailabilityTag userProfileRef={userProfile} />;
  } else {
    return null;
  }
};
